'use client';

import React, { useState, useEffect, useId } from 'react';
import { Typography } from '@/shared/components/typography/SafeTypography';
import { Popover, Button, Space, Divider } from 'antd';
import {
  MoreOutlined,
  ToolOutlined,
  MobileOutlined,
} from '@ant-design/icons';
import { useTheme } from '@/shared/theme';
import styles from './tables.module.css';

const { Text } = Typography;

export interface CardToolAction {
  key: string;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  type?: 'primary' | 'default' | 'dashed' | 'link';
}

export interface CardToolGroup {
  title?: string;
  actions: CardToolAction[];
}

interface CardToolbarProps {
  /** Tool groups to display in the popover */
  toolGroups: CardToolGroup[];
  /** Card title for context */
  cardTitle?: string;
  /** Whether to show mobile-optimized version */
  forceMobile?: boolean;
  /** Custom trigger button */
  trigger?: React.ReactNode;
  /** Placement of the popover */
  placement?:
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'topLeft'
    | 'topRight'
    | 'bottomLeft'
    | 'bottomRight';
  /** Additional CSS classes */
  className?: string;
}

/**
 * CardToolbar — popover tools with ≥44px touch targets and focus-visible.
 * Keyboard: trigger is a button; actions are focusable buttons; Esc closes popover.
 */
export function CardToolbar({
  toolGroups,
  cardTitle,
  forceMobile = false,
  trigger,
  placement = 'bottomRight',
  className = '',
}: CardToolbarProps) {
  const { token } = useTheme();
  const [isMobile, setIsMobile] = useState(false);
  const [visible, setVisible] = useState(false);
  const panelId = useId();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isActuallyMobile = isMobile || forceMobile;

  const handleActionClick = (action: CardToolAction) => {
    if (!action.disabled) {
      action.onClick();
      setVisible(false);
    }
  };

  const renderToolGroup = (group: CardToolGroup, groupIndex: number) => (
    <div key={groupIndex} style={{ marginBottom: groupIndex < toolGroups.length - 1 ? 12 : 0 }}>
      {group.title && (
        <Text
          type="secondary"
          style={{
            fontSize: 11,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: 8,
            display: 'block',
          }}
          id={`${panelId}-group-${groupIndex}`}
        >
          {group.title}
        </Text>
      )}
      <Space
        orientation={isActuallyMobile ? 'vertical' : 'horizontal'}
        size={isActuallyMobile ? 8 : 4}
        style={{ width: '100%' }}
        wrap={!isActuallyMobile}
        role="group"
        aria-labelledby={group.title ? `${panelId}-group-${groupIndex}` : undefined}
      >
        {group.actions.map((action) => (
          <Button
            key={action.key}
            type={action.type || 'default'}
            danger={action.danger}
            disabled={action.disabled}
            icon={action.icon}
            onClick={() => handleActionClick(action)}
            size={isActuallyMobile ? 'middle' : 'small'}
            className={styles.cardToolAction}
            aria-label={action.label}
            style={{
              minWidth: isActuallyMobile ? '100%' : undefined,
              justifyContent: isActuallyMobile ? 'flex-start' : 'center',
              height: isActuallyMobile ? 44 : 40,
              fontSize: isActuallyMobile ? 14 : 12,
              cursor: action.disabled ? 'not-allowed' : 'pointer',
            }}
          >
            {action.label}
          </Button>
        ))}
      </Space>
      {groupIndex < toolGroups.length - 1 && <Divider style={{ margin: '12px 0 8px 0' }} />}
    </div>
  );

  const popoverContent = (
    <div
      id={panelId}
      role="menu"
      aria-label={cardTitle ? `${cardTitle} tools` : 'Card tools'}
      style={{
        width: isActuallyMobile ? 280 : 220,
        maxWidth: isActuallyMobile ? '90vw' : 300,
      }}
    >
      {cardTitle && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 12,
            paddingBottom: 8,
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          <ToolOutlined style={{ color: token.colorPrimary }} aria-hidden />
          <Text strong style={{ fontSize: 13 }}>
            {cardTitle} Tools
          </Text>
          {isActuallyMobile && (
            <MobileOutlined
              style={{
                color: token.colorTextTertiary,
                fontSize: 12,
                marginLeft: 'auto',
              }}
              aria-hidden
            />
          )}
        </div>
      )}

      {toolGroups.map((group, index) => renderToolGroup(group, index))}

      {toolGroups.length === 0 && (
        <p className={styles.cardToolEmpty}>No tools available for this card</p>
      )}
    </div>
  );

  const defaultTrigger = (
    <Button
      type="text"
      icon={<MoreOutlined />}
      className={`${styles.cardToolTrigger} ${className}`.trim()}
      aria-label={cardTitle ? `${cardTitle} tools` : 'Card tools'}
      aria-haspopup="menu"
      aria-expanded={visible}
      aria-controls={visible ? panelId : undefined}
    />
  );

  return (
    <Popover
      content={popoverContent}
      title={null}
      trigger="click"
      placement={placement}
      open={visible}
      onOpenChange={setVisible}
      classNames={{ root: 'card-toolbar-popover' }}
      styles={{
        content: {
          padding: isActuallyMobile ? 16 : 12,
        },
      }}
    >
      {trigger || defaultTrigger}
    </Popover>
  );
}

/**
 * Hook to create common card tool groups
 */
export function useCardTools() {
  const createToolGroup = (title: string, actions: CardToolAction[]): CardToolGroup => ({
    title,
    actions,
  });

  const createAction = (
    key: string,
    label: string,
    onClick: () => void,
    options: Partial<CardToolAction> = {}
  ): CardToolAction => ({
    key,
    label,
    onClick,
    ...options,
  });

  return { createToolGroup, createAction };
}

export default CardToolbar;
